<template>
  <MediumModal
    :is-open="confirmModalOpen"
    @close="closeConfirmModal"
  >
    <div class="flex flex-col gap-y-6 py-4 px-8">
      <ArrowRightCircleIcon class="w-8 h-8 text-gray-800 shrink-0 relative top-0.5 self-center" />
      <h3 class="font-semibold text-xl text-blue-600 self-center">Confirm Movement</h3>

      <div class="text-center flex flex-col gap-y-2">
        <p class="text-base">
          <span class="font-semibold"
            >{{ spaceManagement.form.qty }} Unit<span v-if="spaceManagement.form.qty > 1">s</span></span
          >
          of sub-lot
          <span class="font-semibold">{{ spaceManagement.state.selectedSubLot.sub_lot_num }}</span>
          will be moved
          <br />
          from
          <span class="font-semibold"
            >{{ spaceManagement.state.selectedLocation.name }}
            {{ spaceManagement.state.selectedSourceSpace.name }}</span
          >
          to
          <span class="font-semibold"
            >{{ spaceManagement.state.selectedLocation.name }}
            {{ spaceManagement.state.selectedDestinationSpace.name }}</span
          >.
        </p>

        <p class="text-sm text-gray-600">{{ moment(Date.now()).format('MMM D, YYYY @ h:mm A') }} by {{ usersName }}</p>
      </div>

      <div class="flex gap-x-5 justify-center">
        <button
          type="button"
          @click="closeConfirmModal"
          class="uppercase font-semibold text-sm py-3 px-8 rounded-lg border-2 border-red-400 bg-red-200 text-red-400"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="confirmMovement"
          class="uppercase font-semibold text-sm py-3 px-8 rounded-lg bg-blue-600 text-gray-100"
        >
          Confirm
        </button>
      </div>
    </div>
  </MediumModal>
  <FloatPanel
    :open="spaceMovementPanel.state.open"
    :widths="[
      ['(max-width: 768px)', '100vw'],
      ['(min-width: 768px)', '500px']
    ]"
    location="right"
    class="bg-gray-100 overflow-y-scroll flex flex-col md:rounded-tl-2xl md:shadow-xl md:border md:border-gray-400"
    @click-outside="() => confirmModalOpen || spaceMovementPanel.close()"
    @esc-pressed="() => confirmModalOpen || spaceMovementPanel.close()"
  >
    <PanelHeader
      title="Initiate Movement"
      @close="spaceMovementPanel.close"
    />

    <form
      @submit.prevent="onSubmit"
      class="px-4 sm:px-8 pb-8 flex flex-col gap-y-4"
    >
      <div class="flex flex-col gap-y-1">
        <div class="flex w-full items-center bg-gray-300 rounded-lg overflow-hidden relative py-3 px-4">
          <span class="font-semibold text-ellipsis overflow-hidden whitespace-nowrap pr-16">{{
            spaceManagement.state.packageName
          }}</span>
          <span
            class="absolute inset-y-0 right-0 pr-6 flex items-center pointer-events-none text-right text-sm font-semibold text-gray-600"
            >Package</span
          >
          <input
            type="hidden"
            name="package_id"
            :value="spaceManagement.form.packageId"
          />
        </div>
        <div class="text-xs text-red-400 font-semibold flex items-center justify-between">
          <span v-if="spaceManagement.form.errors.packageId">{{ spaceManagement.form.errors.packageId }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-y-1">
        <div
          class="flex w-full border-gray-400 items-center border-2 rounded-lg overflow-hidden"
          :class="{
            'error-field error-field-group':
              spaceManagement.form.errors.locationId || spaceManagement.form.errors.sourceSpaceId
          }"
        >
          <label
            for="source_location_id"
            class="sr-only"
            >Move From Location:</label
          >
          <select
            class="w-5/12 border-none font-semibold focus:ring-0 focus:text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap"
            name="source_location_id"
            id="source_location_id"
            v-model="spaceManagement.form.locationId"
            :class="{
              'text-gray-600': !spaceManagement.form.locationId,
              uppercase: spaceManagement.form.locationId
            }"
          >
            <option
              :value="null"
              hidden
            >
              Location
            </option>
            <option
              v-for="location in spaceManagement.state.locations"
              :value="location.id"
            >
              {{ location.name }}
            </option>
          </select>

          <div class="horizontal-divider"></div>

          <label
            for="source_space_id"
            class="sr-only"
            >Move From Space:</label
          >

          <select
            class="w-7/12 border-none font-semibold focus:ring-0 focus:text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap"
            name="source_space_id"
            id="source_space_id"
            :disabled="!spaceManagement.state.sourceSpaces.length"
            v-model="spaceManagement.form.sourceSpaceId"
            :class="{
              'text-gray-600': !spaceManagement.form.sourceSpaceId
            }"
          >
            <option
              :value="null"
              hidden
            >
              Space
            </option>
            <option
              v-for="space in spaceManagement.state.sourceSpaces"
              :key="space.id"
              :value="space.id"
            >
              {{ space.name }}
            </option>
          </select>
        </div>
        <div class="text-xs text-red-400 font-semibold flex items-center justify-between">
          <span v-if="spaceManagement.form.errors.locationId">{{ spaceManagement.form.errors.locationId }}</span>
          <span v-if="spaceManagement.form.errors.sourceSpaceId">{{ spaceManagement.form.errors.sourceSpaceId }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-y-1">
        <div
          class="border-2 border-gray-400 rounded-lg overflow-hidden relative"
          :class="{
            'error-field error-field-group': spaceManagement.form.errors.subLotId
          }"
        >
          <select
            class="w-full border-none font-semibold focus:ring-0 focus:text-gray-800"
            :class="{
              'text-gray-600': !spaceManagement.form.subLotId
            }"
            name="sub_lot_id"
            id="sub_lot_id"
            :disabled="!spaceManagement.state.selectedLocation?.sub_lots?.length"
            v-model="spaceManagement.form.subLotId"
          >
            <option
              :value="null"
              hidden
            >
              Sub-lot
            </option>
            <option
              v-for="subLot in spaceManagement.state.selectedLocation?.sub_lots ?? []"
              :key="subLot.sub_lot_num"
              :value="subLot.sub_lot_num"
            >
              {{ subLot.sub_lot_num }}
            </option>
          </select>
          <span
            v-if="spaceManagement.form.subLotId"
            class="absolute inset-y-0 right-0 pr-10 flex items-center pointer-events-none text-right text-xs font-semibold text-gray-500"
          >
            {{ spaceManagement.state.maxMoveQty }} Units
          </span>
        </div>
        <div class="text-xs text-red-400 font-semibold flex items-center justify-between">
          <span v-if="spaceManagement.form.errors.subLotId">{{ spaceManagement.form.errors.subLotId }}</span>
        </div>
      </div>

      <div class="transfer-divider flex justify-center">
        <div class="bg-gray-100 px-2">
          <ArrowDownCircleIcon class="w-6 h-6 text-gray-800" />
        </div>
      </div>

      <div class="flex flex-col gap-y-1">
        <div
          class="flex w-full items-center border-2 border-gray-400 rounded-lg overflow-hidden"
          :class="{
            'error-field error-field-group': spaceManagement.form.errors.destinationSpaceId
          }"
        >
          <span
            class="w-5/12 px-4 font-semibold text-sm overflow-hidden text-ellipsis whitespace-nowrap"
            :class="{
              'text-gray-600': !spaceManagement.state.selectedLocation,
              uppercase: spaceManagement.state.selectedLocation
            }"
          >
            {{ spaceManagement.state.selectedLocation?.name ?? 'Location' }}
          </span>

          <div class="horizontal-divider"></div>

          <label
            for="destination_space_id"
            class="sr-only"
            >Move To:</label
          >
          <select
            name="destination_space_id"
            id="destination_space_id"
            v-model="spaceManagement.form.destinationSpaceId"
            class="w-7/12 border-none font-semibold focus:ring-0 focus:text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap"
            :disabled="!spaceManagement.state.destinationSpaces?.length"
            :class="{
              'text-gray-600': !spaceManagement.form.destinationSpaceId
            }"
          >
            <option
              :value="null"
              hidden
            >
              Space
            </option>
            <option
              v-for="space in spaceManagement.state.destinationSpaces"
              :key="space.id"
              :value="space.id"
            >
              {{ space.name }}
            </option>
          </select>
        </div>
        <div class="text-xs text-red-400 font-semibold flex items-center justify-end">
          <span v-if="spaceManagement.form.errors.destinationSpaceId">{{
            spaceManagement.form.errors.destinationSpaceId
          }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-y-1">
        <div
          class="flex w-full gap-4"
          :class="{
            'error-field': spaceManagement.form.errors.qty
          }"
        >
          <div class="relative flex-1">
            <input
              type="text"
              name="qty"
              id="qty"
              v-model="spaceManagement.form.qty"
              class="border-2 border-gray-400 w-full rounded-lg font-semibold"
              placeholder="Quantity"
              :disabled="!spaceManagement.state.maxMoveQty"
            />
            <span
              v-if="spaceManagement.state.maxMoveQty"
              class="absolute inset-y-0 right-0 pr-6 text-right text-xs font-semibold flex items-center pointer-events-none text-gray-500"
            >
              / {{ spaceManagement.state.maxMoveQty }} Units
            </span>
          </div>

          <button
            :disabled="spaceManagement.form.processing"
            type="submit"
            class="btn btn-primary px-12 font-semibold uppercase"
          >
            Move
          </button>
        </div>
        <div class="text-xs text-red-400 font-semibold flex items-center">
          <span v-if="spaceManagement.form.errors.qty">{{ spaceManagement.form.errors.qty }}</span>
        </div>
      </div>
    </form>

    <div
      v-if="spaceManagement.state.selectedLocation"
      class="border-t border-gray-400 bg-gray-200 flex-1 mt-4 pt-4"
    >
      <h4 class="font-semibold text-lg px-4 sm:px-8 pt-4">Recent Movements</h4>
      <ol class="divide-y divide-gray-400">
        <li
          v-for="recentMovement in spaceManagement.state.selectedLocation.recent_movements"
          class="py-4 px-4 sm:px-8 flex gap-x-3"
        >
          <ArrowRightCircleIcon class="w-6 h-6 text-gray-800 shrink-0 relative top-0.5" />
          <div>
            <p>
              <span class="font-semibold"
                >{{ recentMovement.qty }} Unit<span v-if="recentMovement.qty > 1">s</span></span
              >
              of sub-lot
              <span class="font-semibold">{{ recentMovement.sub_lot_num }}</span>
              <span v-if="recentMovement.qty === 1"> was </span><span v-if="recentMovement.qty > 1"> were </span>
              moved from
              <span class="font-semibold">{{ recentMovement.source_location }} {{ recentMovement.source_space }}</span>
              to
              <span class="font-semibold"
                >{{ recentMovement.destination_location }} {{ recentMovement.destination_space }}</span
              >.
            </p>
            <p class="text-xs text-gray-600 pt-2">
              {{ moment(recentMovement.created_at).format('MMM D, YYYY @ h:mm A') }} by: {{ recentMovement.user_name }}
            </p>
          </div>
        </li>
      </ol>
    </div>
  </FloatPanel>
</template>

<style scoped>
.transfer-divider {
  background: linear-gradient(theme('colors.gray.800'), theme('colors.gray.800')) no-repeat center/100% 2px;
}
.horizontal-divider {
  height: calc(100% - 20px);
  width: 1px;
  @apply bg-gray-400;
}
.error-field-group {
  @apply bg-red-200 border-red-400;
}
.error-field {
  select {
    background-color: transparent !important;
    @apply text-red-400;
  }
  input {
    @apply border-red-400 bg-red-200 text-red-400;
  }
  input::placeholder {
    color: theme('colors.red.400') !important;
  }
}
</style>

<script setup>
import { computed, ref } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { FloatPanel } from '@/Components/Panels'
import spaceManagement from '@/Pages/Packages/SpaceManagement'
import spaceMovementPanel from '@/Pages/Packages/SpaceMovementPanel'
import { ArrowDownCircleIcon, ArrowRightCircleIcon } from '@heroicons-2/vue/24/outline'
import moment from 'moment'
import MediumModal from '@/Components/Modals/MediumModal.vue'
import PanelHeader from '@/Components/Panels/PanelHeader.vue'

const page = usePage()
const usersName = computed(() => {
  return `${page.props.auth.user.first_name} ${page.props.auth.user.last_name}`
})

const confirmModalOpen = ref(false)
const closeConfirmModal = () => (confirmModalOpen.value = false)

const confirmMovement = () => {
  closeConfirmModal()
  spaceManagement.moveInventory()
}

const onSubmit = () => {
  const isValid = spaceManagement.validateForm()

  if (!isValid) {
    return
  }

  confirmModalOpen.value = true
}
</script>
